import { registerBlockType } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { InnerBlocks, useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, RangeControl } from '@wordpress/components';
import { columns } from '@wordpress/icons';

const ALLOWED_BLOCKS = ['core/column', 'puroclean/card', 'core/group', 'core/cover', 'core/paragraph', 'core/heading', 'core/image', 'core/button', 'core/list', 'puroclean/flexible-columns', 'puroclean/card', 'core/columns'];

const TEMPLATE = [
    ['core/column', {}, [
        ['core/paragraph', { placeholder: 'Column 1 content...' }]
    ]],
    ['core/column', {}, [
        ['core/paragraph', { placeholder: 'Column 2 content...' }]
    ]],
    ['core/column', {}, [
        ['core/paragraph', { placeholder: 'Column 3 content...' }]
    ]],
    ['core/column', {}, [
        ['core/paragraph', { placeholder: 'Column 4 content...' }]
    ]]
];

registerBlockType('puroclean/flexible-columns', {
    icon: columns,
    edit: ({ attributes, setAttributes }) => {
        const { 
            columns: columnBreakpoints, 
            gap, 
            minColumnWidth, 
            verticalAlignment,
            backgroundColor
        } = attributes;

        const blockProps = useBlockProps({
            className: 'flexible-columns-editor'
        });

        // Override InnerBlocks default styles to force grid layout using double class specificity trick
        const editorStyles = `
            .flexible-columns-editor.flexible-columns-editor {
                position: relative;
                width: 100%;
                max-width: 100%;
            }
            
            .flexible-columns-editor.flexible-columns-editor .flexible-columns-preview {
                position: relative;
                width: 100%;
            }
            
            /* Force grid layout for columns (horizontal side by side) */
            .flexible-columns-editor.flexible-columns-editor .block-editor-inner-blocks .block-editor-block-list__layout {
                display: grid !important;
                grid-template-columns: repeat(${columnBreakpoints.lg}, minmax(${minColumnWidth}, 1fr)) !important;
                gap: ${gap === 'gap-2' ? '0.5rem' : gap === 'gap-4' ? '1rem' : gap === 'gap-6' ? '1.5rem' : gap === 'gap-8' ? '2rem' : '3rem'} !important;
                align-items: ${verticalAlignment === 'center' ? 'center' : verticalAlignment === 'end' ? 'end' : verticalAlignment === 'stretch' ? 'stretch' : 'start'} !important;
                width: 100%;
                margin: 0;
                padding: 0;
            }
            
            /* Constrain grid children (columns) */
            .flexible-columns-editor.flexible-columns-editor .block-editor-inner-blocks .block-editor-block-list__layout > .block-editor-block-list__block {
                margin: 0;
                max-width: 100%;
                min-width: 0;
            }
            
            /* Target core columns - make content inside stack vertically */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column.wp-block-column {
                margin: 0 !important;
                max-width: 100% !important;
                min-width: 0 !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: flex-start !important;
            }
            
            /* Make content inside columns stack vertically */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .block-editor-inner-blocks .block-editor-block-list__layout {
                display: flex !important;
                flex-direction: column !important;
                gap: 0.5rem !important;
            }
            
            /* Target card blocks specifically */
            .flexible-columns-editor.flexible-columns-editor [data-type="puroclean/card"] {
                max-width: 100%;
                min-width: 0;
                margin-bottom: 0 !important;
            }
            
            .flexible-columns-editor.flexible-columns-editor [data-type="puroclean/card"] .card-block {
                max-width: 100%;
                box-sizing: border-box;
                height: auto;
            }
            
            /* Ensure card content also stacks vertically */
            .flexible-columns-editor.flexible-columns-editor [data-type="puroclean/card"] .block-editor-inner-blocks .block-editor-block-list__layout {
                display: flex !important;
                flex-direction: column !important;
                gap: 0.5rem !important;
            }
            
            /* Remove default WordPress block margins within our layout - ALL BLOCKS IN COLUMNS */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .block-editor-block-list__block {
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
            }
            
            /* Force ALL block types within columns to stack properly */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column [data-type] {
                width: 100% !important;
                max-width: 100% !important;
                display: block !important;
            }
            
            /* Ensure nested content within ANY block type stacks vertically */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .block-editor-inner-blocks .block-editor-block-list__layout {
                display: flex !important;
                flex-direction: column !important;
                gap: 0.5rem !important;
                width: 100% !important;
            }
            
            /* Override ANY display properties that could cause horizontal layouts within columns */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column * {
                max-width: 100% !important;
            }
            
            /* Specifically target common block types that might have display issues */
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-paragraph,
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-heading,
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-image,
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-list,
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-button,
            .flexible-columns-editor.flexible-columns-editor .wp-block-column .wp-block-group {
                width: 100% !important;
                max-width: 100% !important;
                display: block !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
            }
        `;

        return (
            <>
                <style>{editorStyles}</style>
                <InspectorControls>
                    <PanelBody title={__('Column Settings', 'puroclean')} initialOpen={true}>
                        <RangeControl
                            label={__('Columns (Base/Mobile)', 'puroclean')}
                            value={columnBreakpoints.base}
                            onChange={(value) => setAttributes({
                                columns: { ...columnBreakpoints, base: value }
                            })}
                            min={1}
                            max={6}
                            help={__('Number of columns on mobile devices', 'puroclean')}
                        />
                        <RangeControl
                            label={__('Columns (Small - sm)', 'puroclean')}
                            value={columnBreakpoints.sm}
                            onChange={(value) => setAttributes({
                                columns: { ...columnBreakpoints, sm: value }
                            })}
                            min={1}
                            max={6}
                            help={__('Number of columns on small screens (640px+)', 'puroclean')}
                        />
                        <RangeControl
                            label={__('Columns (Medium - md)', 'puroclean')}
                            value={columnBreakpoints.md}
                            onChange={(value) => setAttributes({
                                columns: { ...columnBreakpoints, md: value }
                            })}
                            min={1}
                            max={6}
                            help={__('Number of columns on medium screens (768px+)', 'puroclean')}
                        />
                        <RangeControl
                            label={__('Columns (Large - lg)', 'puroclean')}
                            value={columnBreakpoints.lg}
                            onChange={(value) => setAttributes({
                                columns: { ...columnBreakpoints, lg: value }
                            })}
                            min={1}
                            max={6}
                            help={__('Number of columns on large screens (1024px+)', 'puroclean')}
                        />
                    </PanelBody>

                    <PanelBody title={__('Layout Settings', 'puroclean')} initialOpen={false}>
                        <SelectControl
                            label={__('Gap Between Columns', 'puroclean')}
                            value={gap}
                            options={[
                                { label: 'Small (0.5rem)', value: 'gap-2' },
                                { label: 'Medium (1rem)', value: 'gap-4' },
                                { label: 'Large (1.5rem)', value: 'gap-6' },
                                { label: 'Extra Large (2rem)', value: 'gap-8' },
                                { label: 'XXL (3rem)', value: 'gap-12' }
                            ]}
                            onChange={(value) => setAttributes({ gap: value })}
                        />

                        <SelectControl
                            label={__('Minimum Column Width', 'puroclean')}
                            value={minColumnWidth}
                            options={[
                                { label: '240px', value: '240px' },
                                { label: '280px', value: '280px' },
                                { label: '320px', value: '320px' },
                                { label: '360px', value: '360px' },
                                { label: '400px', value: '400px' }
                            ]}
                            onChange={(value) => setAttributes({ minColumnWidth: value })}
                            help={__('Columns will never get smaller than this width', 'puroclean')}
                        />

                        <SelectControl
                            label={__('Vertical Alignment', 'puroclean')}
                            value={verticalAlignment}
                            options={[
                                { label: 'Top', value: 'start' },
                                { label: 'Center', value: 'center' },
                                { label: 'Bottom', value: 'end' },
                                { label: 'Stretch', value: 'stretch' }
                            ]}
                            onChange={(value) => setAttributes({ verticalAlignment: value })}
                        />
                    </PanelBody>

                    <PanelBody title={__('Background', 'puroclean')} initialOpen={false}>
                        <SelectControl
                            label={__('Background Color', 'puroclean')}
                            value={backgroundColor}
                            options={[
                                { label: 'Transparent', value: 'transparent' },
                                { label: 'White', value: 'bg-white' },
                                { label: 'Gray 50', value: 'bg-gray-50' },
                                { label: 'Gray 100', value: 'bg-gray-100' },
                                { label: 'Primary', value: 'bg-primary' },
                                { label: 'Secondary', value: 'bg-secondary' }
                            ]}
                            onChange={(value) => setAttributes({ backgroundColor: value })}
                        />
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps}>
                    <div className="flexible-columns-preview" style={{ 
                        border: '2px dashed #d1d5db', 
                        padding: '1rem', 
                        borderRadius: '0.5rem',
                        backgroundColor: '#f9fafb'
                    }}>
                        <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6b7280',
                            marginBottom: '0.5rem',
                            textAlign: 'center',
                            fontWeight: '600'
                        }}>
                            Flexible Columns: {columnBreakpoints.base}/{columnBreakpoints.sm}/{columnBreakpoints.md}/{columnBreakpoints.lg}
                        </div>
                        <InnerBlocks
                            allowedBlocks={ALLOWED_BLOCKS}
                            template={TEMPLATE}
                            templateLock={false}
                            renderAppender={() => (
                                <InnerBlocks.ButtonBlockAppender />
                            )}
                        />
                    </div>
                </div>
            </>
        );
    },
    save: () => {
        return <InnerBlocks.Content />;
    }
}); 